import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button } from "antd";
import { PlusCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Common/Table";
import ChartModal from "./Create";
import {
  ChartGetParams,
  ChartTableRow,
  useChartTableGet,
} from "../../../../../../hooks/api/charts";
import { useParams, useSearchParams } from "react-router";
import { getSorterParams } from "../../../../../../utils";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { dateFormatShort } from "../../../../../../contstant";
import { useVaadTabs } from "../context";

interface ChartsTabProps {
  activeTab?: string;
}

function ToolBar({ onCreate }: { onCreate: () => void }) {
  return [
    <Button
      key="create"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Add New Chart
    </Button>,
  ];
}

export default function ChartsTab({
  activeTab,
}: ChartsTabProps): React.ReactNode | null {
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { id } = useParams();
  const [, setSearchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const chartTableGet = useChartTableGet();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedChartId, setSelectedChartId] = useState<string>();
  const { middahOptions } = useVaadTabs();

  useEffect(() => {
    if (activeTab === "charts") {
      setTableKey(Date.now());
    }
  }, [activeTab]);

  const columns: ProColumns<ChartTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      hideInSearch: true,
    },
    {
      title: "Middah",
      dataIndex: "middahId",
      key: "middahId",
      sorter: true,
      valueType: "select",
      valueEnum: Object.fromEntries(
        middahOptions.map((m) => [m.value, { text: m.label }]),
      ),
      render: (_, record: ChartTableRow) => (
        <span>{record.middah?.name || "-"}</span>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startTime",
      key: "startTime",
      sorter: true,
      hideInSearch: true,
      render: (_, record) =>
        record.startTime
          ? dayjs.utc(record.startTime).format(dateFormatShort)
          : "—",
    },
    {
      title: "End Date",
      dataIndex: "endTime",
      key: "endTime",
      sorter: true,
      hideInSearch: true,
      render: (_, record) =>
        record.endTime
          ? dayjs.utc(record.endTime).format(dateFormatShort)
          : "—",
    },
  ];

  const handleEdit = (record: ChartTableRow) => {
    setSelectedChartId(record.id);
    setEditModalOpen(true);
  };

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ChartTableRow>>> => {
    const newParams: ChartGetParams = {
      vaadId: id,
      page: current || 1,
      orderByColumn: "createdAt",
      pageSize: pageSize || 10,
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return chartTableGet.fetch(newParams).then((response) => {
      if (response) {
        const { data, total } = response;

        return {
          data: data || [],
          success: true,
          total,
        };
      }

      return { data: [], success: false, total: 0 };
    });
  };

  return (
    <>
      <Table<ChartTableRow>
        key={`charts-${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className="tableCustomPadding"
        headerTitle={t("pages.charts.title")}
        request={tableRequest}
        toolBarRender={() => [
          <ToolBar key="toolbar" onCreate={() => setCreateOpen(true)} />,
        ]}
        columns={columns}
        actions={[
          [
            "edit",
            ({ record }) => ({
              title: "Edit",
              onClick: (e: MouseEvent) => {
                e.stopPropagation();
                handleEdit(record);
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: "none",
            paddingBottom: 0,
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by chart name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-charts",
          persistenceType: "localStorage",
        }}
      />
      <ChartModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        vaadId={id || ""}
        onSave={() => setTableKey(Date.now())}
        mode="create"
      />
      <ChartModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={() => setTableKey(Date.now())}
        vaadId={id || ""}
        chartId={selectedChartId}
        mode="edit"
      />
    </>
  );
}
